import React, { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import TopBar from "../../components/TopBar";
import {
  BillingContext,
  HierarchyBillingCustomer,
  HierarchyIntegratorBilling,
  MasterPlan,
  RetailPlan,
  createMasterPlan,
  createRetailPlan,
  getBillingContext,
  getCustomerBillingView,
  listHierarchyCustomers,
  listHierarchyIntegrators,
  listMasterPlans,
  listRetailPlans,
  saveAiMonitoringRetailPricing,
  saveIntegratorEntitlements,
} from "../../services/billingHierarchyService";
import AdminPlanAccessPanel from "./AdminPlanAccessPanel";
import "./Billing.css";

type Props = { user?: any; onSignOut?: () => void };

type EntitlementDraft = Record<
  string,
  { enabled: boolean; price: string }
>;

function emailOf(user: any) {
  return (
    user?.signInDetails?.loginId ||
    user?.attributes?.email ||
    user?.username ||
    ""
  );
}

function money(value: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(Number(value || 0));
}

function tokenRate(value: number, currency = "USD") {
  return `${currency} ${Number(value || 0).toFixed(6)}`;
}

function analyticsLabel(value: string) {
  return String(value || "NONE").trim().toUpperCase() === "NONE"
    ? "Recording only"
    : `${String(value).charAt(0)}${String(value).slice(1).toLowerCase()} analytics`;
}

function planSummary(plan: MasterPlan) {
  if (plan.billingModel === "PAYG") return "Usage-based · camera settings unchanged";
  return `${plan.retentionDays} days · ${plan.resolution?.width || 0}×${
    plan.resolution?.height || 0
  } · ${plan.fps} FPS · ${analyticsLabel(plan.analyticsTier)}`;
}

function OwnerView({
  onError,
  onMessage,
}: {
  onError: (message: string) => void;
  onMessage: (message: string) => void;
}) {
  const [plans, setPlans] = useState<MasterPlan[]>([]);
  const [integrators, setIntegrators] = useState<HierarchyIntegratorBilling[]>([]);
  const [selectedIntegratorId, setSelectedIntegratorId] = useState("");
  const [entitlements, setEntitlements] = useState<EntitlementDraft>({});
  const [aiDraft, setAiDraft] = useState({
    enabled: false,
    wholesaleInput: "0.175834",
    wholesaleOutput: "0.703334",
  });
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({
    name: "",
    billingModel: "FIXED" as "FIXED" | "PAYG",
    retentionDays: "7",
    analyticsTier: "BASIC" as "NONE" | "BASIC" | "ADVANCED",
    width: "1920",
    height: "1080",
    fps: "15",
    bitrateKbps: "2048",
    price: "0",
  });

  const load = useCallback(async () => {
    const [masterPlans, partnerItems] = await Promise.all([
      listMasterPlans(),
      listHierarchyIntegrators(),
    ]);
    setPlans(masterPlans);
    setIntegrators(partnerItems);
    setSelectedIntegratorId((current) =>
      current && partnerItems.some((item) => item.partnerId === current)
        ? current
        : partnerItems[0]?.partnerId || "",
    );
  }, []);

  useEffect(() => {
    void load().catch((error) =>
      onError(error instanceof Error ? error.message : "Unable to load Owner billing."),
    );
  }, [load, onError]);

  const selectedIntegrator = useMemo(
    () =>
      integrators.find((item) => item.partnerId === selectedIntegratorId) ||
      null,
    [integrators, selectedIntegratorId],
  );

  useEffect(() => {
    if (!selectedIntegrator) {
      setEntitlements({});
      setAiDraft({
        enabled: false,
        wholesaleInput: "0.175834",
        wholesaleOutput: "0.703334",
      });
      return;
    }
    const next: EntitlementDraft = {};
    for (const plan of plans) {
      const current = selectedIntegrator.entitlements.find(
        (item) => item.masterPlanId === plan.planId,
      );
      next[plan.planId] = {
        enabled: current?.enabled === true,
        price: String(
          current?.wholesaleMonthlyPrice ??
            plan.defaultWholesaleMonthlyPrice ??
            0,
        ),
      };
    }
    setEntitlements(next);
    setAiDraft({
      enabled: selectedIntegrator.aiMonitoring.enabled,
      wholesaleInput: String(
        selectedIntegrator.aiMonitoring.wholesaleInputPerMillionTokens ??
          0.175834,
      ),
      wholesaleOutput: String(
        selectedIntegrator.aiMonitoring.wholesaleOutputPerMillionTokens ??
          0.703334,
      ),
    });
  }, [plans, selectedIntegrator]);

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await createMasterPlan({
        name: draft.name.trim(),
        billingModel: draft.billingModel,
        retentionDays: Number(draft.retentionDays),
        analyticsTier: draft.analyticsTier,
        resolution: {
          width: Number(draft.width),
          height: Number(draft.height),
        },
        fps: Number(draft.fps),
        bitrateKbps: Number(draft.bitrateKbps),
        currency: "USD",
        defaultWholesaleMonthlyPrice: Number(draft.price),
        enabled: true,
      });
      setDraft((current) => ({ ...current, name: "", price: "0" }));
      await load();
      onMessage("Owner billing plan created.");
    } catch (error) {
      onError(error instanceof Error ? error.message : "Unable to create Owner plan.");
    } finally {
      setBusy(false);
    }
  }

  async function saveIntegratorPlans() {
    if (!selectedIntegrator) return;
    setBusy(true);
    try {
      const updated = await saveIntegratorEntitlements(selectedIntegrator.partnerId, {
        plans: plans.map((plan) => ({
          masterPlanId: plan.planId,
          enabled: entitlements[plan.planId]?.enabled === true,
          wholesaleMonthlyPrice: Number(entitlements[plan.planId]?.price || 0),
        })),
        aiMonitoring: {
          enabled: aiDraft.enabled,
          wholesaleInputPerMillionTokens: Number(
            aiDraft.wholesaleInput || 0,
          ),
          wholesaleOutputPerMillionTokens: Number(
            aiDraft.wholesaleOutput || 0,
          ),
        },
      });
      setIntegrators((current) =>
        current.map((item) =>
          item.partnerId === updated.partnerId
            ? { ...item, ...updated }
            : item,
        ),
      );
      onMessage(`Plans and AI Monitoring pricing saved for ${updated.displayName}.`);
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : "Unable to assign Owner plans to Integrator.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="billing-role-layout">
      <section className="billing-section">
        <div className="billing-section__heading">
          <div>
            <div className="billing-eyebrow">Owner</div>
            <h2>Master billing plans</h2>
            <p>Create the products and original Owner price.</p>
          </div>
        </div>
        <form className="billing-plan-builder" onSubmit={create}>
          <label><span>Billing model</span><select value={draft.billingModel} onChange={(e) => setDraft({ ...draft, billingModel: e.target.value as typeof draft.billingModel })}><option value="FIXED">Fixed monthly plan</option><option value="PAYG">Pay as you go</option></select></label>
          <label><span>Plan name</span><input required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
          <label style={{ display: draft.billingModel === "PAYG" ? "none" : undefined }}><span>Recording days</span><input required min="1" type="number" value={draft.retentionDays} onChange={(e) => setDraft({ ...draft, retentionDays: e.target.value })} /></label>
          <label style={{ display: draft.billingModel === "PAYG" ? "none" : undefined }}><span>Analytics</span><select value={draft.analyticsTier} onChange={(e) => setDraft({ ...draft, analyticsTier: e.target.value as typeof draft.analyticsTier })}><option value="NONE">None (recording only)</option><option value="BASIC">Basic</option><option value="ADVANCED">Advanced</option></select></label>
          <label style={{ display: draft.billingModel === "PAYG" ? "none" : undefined }}><span>Width</span><input required min="160" type="number" value={draft.width} onChange={(e) => setDraft({ ...draft, width: e.target.value })} /></label>
          <label style={{ display: draft.billingModel === "PAYG" ? "none" : undefined }}><span>Height</span><input required min="120" type="number" value={draft.height} onChange={(e) => setDraft({ ...draft, height: e.target.value })} /></label>
          <label style={{ display: draft.billingModel === "PAYG" ? "none" : undefined }}><span>FPS</span><input required min="1" type="number" value={draft.fps} onChange={(e) => setDraft({ ...draft, fps: e.target.value })} /></label>
          <label style={{ display: draft.billingModel === "PAYG" ? "none" : undefined }}><span>Bitrate (kbit/s)</span><input required min="64" type="number" value={draft.bitrateKbps} onChange={(e) => setDraft({ ...draft, bitrateKbps: e.target.value })} /></label>
          <label style={{ display: draft.billingModel === "PAYG" ? "none" : undefined }}><span>Owner price / month</span><input required min="0" step="0.01" type="number" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} /></label>
          <button className="billing-primary" type="submit" disabled={busy}>{busy ? "Saving…" : "Create plan"}</button>
        </form>
        <div className="billing-card-grid">
          {plans.map((plan) => (
            <article key={plan.planId} className="billing-plan-card">
              <div className="billing-plan-card__header"><div><h3>{plan.name}</h3><span>{planSummary(plan)}</span></div><strong>{plan.billingModel === "PAYG" ? "Usage based" : `${money(plan.defaultWholesaleMonthlyPrice || 0, plan.currency)}/mo`}</strong></div>
            </article>
          ))}
        </div>
      </section>

      <section className="billing-section">
        <div className="billing-section__heading">
          <div>
            <div className="billing-eyebrow">Owner → Integrator</div>
            <h2>Integrator plan access</h2>
            <p>Choose exactly which plans an Integrator receives and the original price visible to that Integrator.</p>
          </div>
          <select className="billing-integrator-select" value={selectedIntegratorId} onChange={(e) => setSelectedIntegratorId(e.target.value)}>
            {integrators.length === 0 && <option value="">No Integrators</option>}
            {integrators.map((item) => <option key={item.partnerId} value={item.partnerId}>{item.displayName}</option>)}
          </select>
        </div>
        {selectedIntegrator && (
          <>
            <div className="billing-entitlement-grid">
              {plans.map((plan) => {
                const entry = entitlements[plan.planId] || { enabled: false, price: "0" };
                return (
                  <label key={plan.planId} className={`billing-entitlement ${entry.enabled ? "billing-entitlement--enabled" : ""}`}>
                    <div className="billing-entitlement__title"><input type="checkbox" checked={entry.enabled} onChange={(e) => setEntitlements((current) => ({ ...current, [plan.planId]: { ...entry, enabled: e.target.checked } }))} /><strong>{plan.name}</strong></div>
                    <span>{planSummary(plan)}</span>
                    <div><small>Owner price for this Integrator</small><input type="number" min="0" step="0.01" value={entry.price} onChange={(e) => setEntitlements((current) => ({ ...current, [plan.planId]: { ...entry, price: e.target.value } }))} /></div>
                  </label>
                );
              })}
            </div>
            <div className="billing-ai-separate">
              <div>
                <div className="billing-eyebrow">Separate add-on</div>
                <h3>AI Monitoring</h3>
                <p>
                  Bill input and output model tokens independently. The suggested
                  rates include the 5.5% OpenRouter funding fee and a true 40%
                  Qumra gross margin.
                </p>
              </div>
              <label className="billing-switch">
                <input
                  type="checkbox"
                  checked={aiDraft.enabled}
                  onChange={(e) =>
                    setAiDraft({ ...aiDraft, enabled: e.target.checked })
                  }
                />{" "}
                Enable for Integrator
              </label>
              <label>
                <span>Wholesale input / 1M tokens</span>
                <input
                  type="number"
                  min="0"
                  step="0.000001"
                  value={aiDraft.wholesaleInput}
                  onChange={(e) =>
                    setAiDraft({ ...aiDraft, wholesaleInput: e.target.value })
                  }
                />
              </label>
              <label>
                <span>Wholesale output / 1M tokens</span>
                <input
                  type="number"
                  min="0"
                  step="0.000001"
                  value={aiDraft.wholesaleOutput}
                  onChange={(e) =>
                    setAiDraft({ ...aiDraft, wholesaleOutput: e.target.value })
                  }
                />
              </label>
            </div>
            <button className="billing-primary" type="button" disabled={busy} onClick={() => void saveIntegratorPlans()}>Save Integrator plans &amp; AI pricing</button>

            {(selectedIntegrator.retailPlans || []).length > 0 && (
              <div className="billing-card-grid">
                {(selectedIntegrator.retailPlans || []).map((plan) => (
                  <article key={plan.retailPlanId} className="billing-plan-card billing-plan-card--retail">
                    <div className="billing-plan-card__header"><div><h3>{plan.name}</h3><span>Integrator retail plan</span></div><strong>{money(plan.retailMonthlyPrice, plan.currency)}/mo</strong></div>
                    <dl className="billing-plan-specs"><dt>Owner price</dt><dd>{money(plan.wholesaleMonthlyPrice || 0, plan.currency)}</dd><dt>Integrator margin</dt><dd>{money(Math.max(0, Number(plan.retailMonthlyPrice || 0) - Number(plan.wholesaleMonthlyPrice || 0)), plan.currency)}</dd></dl>
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function IntegratorView({
  context,
  onContext,
  onError,
  onMessage,
}: {
  context: BillingContext;
  onContext: (context: BillingContext) => void;
  onError: (message: string) => void;
  onMessage: (message: string) => void;
}) {
  const [masters, setMasters] = useState<MasterPlan[]>([]);
  const [retailPlans, setRetailPlans] = useState<RetailPlan[]>([]);
  const [customers, setCustomers] = useState<HierarchyBillingCustomer[]>([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ masterPlanId: "", name: "", price: "" });
  const [aiDraft, setAiDraft] = useState({
    enabled: Boolean(context.aiMonitoring?.enabled),
    retailInput: String(
      context.aiMonitoring?.retailInputPerMillionTokens ?? 0.175834,
    ),
    retailOutput: String(
      context.aiMonitoring?.retailOutputPerMillionTokens ?? 0.703334,
    ),
  });

  const load = useCallback(async () => {
    const [masterItems, retailItems, customerItems] = await Promise.all([
      listMasterPlans(),
      listRetailPlans(),
      listHierarchyCustomers(),
    ]);
    setMasters(masterItems);
    setRetailPlans(retailItems);
    setCustomers(customerItems);
    setDraft((current) => ({
      ...current,
      masterPlanId: current.masterPlanId || masterItems[0]?.planId || "",
    }));
  }, []);

  useEffect(() => {
    void load().catch((error) =>
      onError(error instanceof Error ? error.message : "Unable to load Integrator billing."),
    );
  }, [load, onError]);

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const selectedMaster = masters.find((plan) => plan.planId === draft.masterPlanId);
      await createRetailPlan({
        masterPlanId: draft.masterPlanId,
        name: draft.name.trim(),
        retailMonthlyPrice: selectedMaster?.billingModel === "PAYG" ? 0 : Number(draft.price),
      });
      setDraft((current) => ({ ...current, name: "", price: "" }));
      await load();
      onMessage("Integrator billing plan created.");
    } catch (error) {
      onError(error instanceof Error ? error.message : "Unable to create Integrator plan.");
    } finally {
      setBusy(false);
    }
  }

  async function saveAiPricing() {
    setBusy(true);
    try {
      const updated = await saveAiMonitoringRetailPricing({
        enabled: aiDraft.enabled,
        retailInputPerMillionTokens: Number(aiDraft.retailInput || 0),
        retailOutputPerMillionTokens: Number(aiDraft.retailOutput || 0),
      });
      setAiDraft({
        enabled: updated.enabled,
        retailInput: String(updated.retailInputPerMillionTokens),
        retailOutput: String(updated.retailOutputPerMillionTokens),
      });
      onContext({
        ...context,
        aiMonitoring: {
          available: context.aiMonitoring?.available === true,
          enabled: updated.enabled,
          wholesaleInputPerMillionTokens:
            context.aiMonitoring?.wholesaleInputPerMillionTokens ?? 0.175834,
          wholesaleOutputPerMillionTokens:
            context.aiMonitoring?.wholesaleOutputPerMillionTokens ?? 0.703334,
          retailInputPerMillionTokens: updated.retailInputPerMillionTokens,
          retailOutputPerMillionTokens: updated.retailOutputPerMillionTokens,
          pricingBasis: context.aiMonitoring?.pricingBasis,
        },
      });
      onMessage("AI Monitoring customer token rates saved.");
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : "Unable to save AI Monitoring pricing.",
      );
    } finally {
      setBusy(false);
    }
  }

  const selectedMaster = masters.find((plan) => plan.planId === draft.masterPlanId);

  return (
    <div className="billing-role-layout">
      <div className="billing-overview-grid">
        <section className="billing-section billing-section--catalog">
          <div className="billing-section__heading">
            <div>
              <div className="billing-eyebrow">Owner → Integrator</div>
              <h2>Plans assigned by Owner</h2>
              <p>
                Products available for you to package and sell to Admin
                accounts.
              </p>
            </div>
            <span className="billing-count-badge">
              {masters.length} {masters.length === 1 ? "plan" : "plans"}
            </span>
          </div>
          <div className="billing-card-grid billing-card-grid--compact">
            {masters.length === 0 ? (
              <div className="billing-empty">
                Owner has not assigned any plans yet.
              </div>
            ) : (
              masters.map((plan) => (
                <article key={plan.planId} className="billing-plan-card">
                  <div className="billing-plan-card__header">
                    <div>
                      <h3>{plan.name}</h3>
                      <span>{planSummary(plan)}</span>
                    </div>
                    <strong>
                      {plan.billingModel === "PAYG"
                        ? "Usage based"
                        : `${money(
                            plan.wholesaleMonthlyPrice || 0,
                            plan.currency,
                          )}/mo`}
                    </strong>
                  </div>
                </article>
              ))
            )}
          </div>
        </section>

        <section className="billing-section billing-ai-card">
          <div className="billing-ai-card__heading">
            <div>
              <div className="billing-eyebrow">Separate add-on</div>
              <h2>AI Monitoring</h2>
            </div>
            <label className="billing-switch">
              <input
                disabled={!context.aiMonitoring?.available}
                type="checkbox"
                checked={aiDraft.enabled}
                onChange={(e) =>
                  setAiDraft({ ...aiDraft, enabled: e.target.checked })
                }
              />{" "}
              Offer to customers
            </label>
          </div>
          <p>
            Set retail rates for model input and output. This remains separate
            from camera plans.
          </p>
          {context.aiMonitoring?.available ? (
            <div className="billing-ai-card__cost">
              <span>Your Qumra cost per 1M tokens</span>
              <strong>
                {tokenRate(
                  context.aiMonitoring.wholesaleInputPerMillionTokens,
                )}{" "}
                input ·{" "}
                {tokenRate(
                  context.aiMonitoring.wholesaleOutputPerMillionTokens,
                )}{" "}
                output
              </strong>
            </div>
          ) : (
            <div className="billing-ai-card__cost billing-ai-card__cost--disabled">
              Owner has not enabled AI Monitoring for this account.
            </div>
          )}
          <div className="billing-ai-card__rates">
            <label>
              <span>Retail input / 1M tokens</span>
              <input
                disabled={!context.aiMonitoring?.available}
                type="number"
                min="0"
                step="0.000001"
                value={aiDraft.retailInput}
                onChange={(e) =>
                  setAiDraft({ ...aiDraft, retailInput: e.target.value })
                }
              />
            </label>
            <label>
              <span>Retail output / 1M tokens</span>
              <input
                disabled={!context.aiMonitoring?.available}
                type="number"
                min="0"
                step="0.000001"
                value={aiDraft.retailOutput}
                onChange={(e) =>
                  setAiDraft({ ...aiDraft, retailOutput: e.target.value })
                }
              />
            </label>
          </div>
          <button
            className="billing-secondary billing-ai-card__save"
            type="button"
            disabled={!context.aiMonitoring?.available || busy}
            onClick={() => void saveAiPricing()}
          >
            Save AI token rates
          </button>
        </section>
      </div>

      <section className="billing-section">
        <div className="billing-section__heading">
          <div>
            <div className="billing-eyebrow">Integrator margin</div>
            <h2>Your Admin plans</h2>
            <p>
              Turn an Owner product into a customer-facing plan and set your
              selling price.
            </p>
          </div>
          <span className="billing-count-badge billing-count-badge--green">
            {retailPlans.length} {retailPlans.length === 1 ? "plan" : "plans"}
          </span>
        </div>
        <form className="billing-retail-builder" onSubmit={create}>
          <label><span>Plan name</span><input required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
          <label><span>Owner plan</span><select required value={draft.masterPlanId} onChange={(e) => setDraft({ ...draft, masterPlanId: e.target.value })}>{masters.map((plan) => <option key={plan.planId} value={plan.planId}>{plan.name} · {money(plan.wholesaleMonthlyPrice || 0, plan.currency)}</option>)}</select></label>
          <label style={{ display: selectedMaster?.billingModel === "PAYG" ? "none" : undefined }}><span>Admin price / month</span><input required disabled={selectedMaster?.billingModel === "PAYG"} min="0" step="0.01" type="number" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} /></label>
          <button className="billing-primary" type="submit" disabled={busy || masters.length === 0}>{busy ? "Creating…" : "Create Admin plan"}</button>
        </form>
        <div className="billing-card-grid">
          {retailPlans.map((plan) => {
            const master = masters.find((item) => item.planId === plan.masterPlanId);
            const cost = Number(master?.wholesaleMonthlyPrice || 0);
            return (
              <article key={plan.retailPlanId} className="billing-plan-card billing-plan-card--retail"><div className="billing-plan-card__header"><div><h3>{plan.name}</h3><span>{master?.name || "Owner plan"}</span></div><strong>{master?.billingModel === "PAYG" ? "Usage based" : `${money(plan.retailMonthlyPrice, plan.currency)}/mo`}</strong></div>{master?.billingModel !== "PAYG" && <dl className="billing-plan-specs"><dt>Your Owner cost</dt><dd>{money(cost, plan.currency)}</dd><dt>Your margin</dt><dd>{money(Math.max(0, plan.retailMonthlyPrice - cost), plan.currency)}</dd></dl>}</article>
            );
          })}
        </div>
      </section>

      <section className="billing-section">
        <div className="billing-section__heading">
          <div>
            <div className="billing-eyebrow">Integrator → Admin</div>
            <h2>Assign plans to Admin accounts</h2>
            <p>
              Choose which retail plans each Admin can use. Owner pricing stays
              private.
            </p>
          </div>
          <span className="billing-count-badge">
            {customers.length} {customers.length === 1 ? "account" : "accounts"}
          </span>
        </div>
        <div className="billing-customer-list">
          {customers.length === 0 ? <div className="billing-empty">No Admin accounts are available under this Integrator.</div> : customers.map((customer) => (
            <article key={customer.customerId} className="billing-customer-card">
              <div className="billing-customer-card__header">
                <div className="billing-customer-identity">
                  <span className="billing-customer-avatar" aria-hidden="true">
                    {(customer.displayName || customer.email || "A").charAt(0).toUpperCase()}
                  </span>
                  <div>
                    <h3>{customer.displayName}</h3>
                    <span>{customer.email}</span>
                  </div>
                </div>
                <span className="billing-customer-type">Admin account</span>
              </div>
              <AdminPlanAccessPanel customer={customer} plans={retailPlans} onError={onError} onMessage={onMessage} />
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function AdminView({ onError }: { onError: (message: string) => void }) {
  const [plans, setPlans] = useState<RetailPlan[]>([]);
  const [items, setItems] = useState<any[]>([]);

  useEffect(() => {
    void Promise.all([listRetailPlans(), getCustomerBillingView()])
      .then(([available, assignments]) => {
        setPlans(available);
        setItems(assignments);
      })
      .catch((error) =>
        onError(error instanceof Error ? error.message : "Unable to load Admin billing."),
      );
  }, [onError]);

  return (
    <div className="billing-role-layout">
      <section className="billing-section">
        <div className="billing-section__heading"><div><div className="billing-eyebrow">Integrator → Admin</div><h2>Plans available to your account</h2><p>Only plans assigned by your Integrator are shown here. Owner plans and Owner wholesale pricing are not visible.</p></div></div>
        <div className="billing-card-grid">
          {plans.length === 0 ? <div className="billing-empty">Your Integrator has not assigned any billing plans to this Admin yet.</div> : plans.map((plan) => (
            <article key={plan.retailPlanId} className="billing-plan-card billing-plan-card--customer"><div className="billing-plan-card__header"><div><h3>{plan.name}</h3><span>{plan.billingModel === "PAYG" ? "Usage-based · camera settings unchanged" : `${plan.configuration.retentionDays} days · ${analyticsLabel(plan.configuration.analyticsTier)}`}</span></div><strong>{plan.billingModel === "PAYG" ? "Usage based" : `${money(plan.retailMonthlyPrice, plan.currency)}/mo`}</strong></div></article>
          ))}
        </div>
        <p className="billing-muted">Assign one of these plans from Camera → Billing Plans. A camera cannot be enabled until a plan is assigned.</p>
      </section>
      <section className="billing-section">
        <div className="billing-section__heading"><div><div className="billing-eyebrow">Your scope</div><h2>Camera plan assignments</h2></div></div>
        <div className="billing-card-grid">
          {items.length === 0 ? <div className="billing-empty">No cameras in your scope have a billing plan yet.</div> : items.map((item) => (
            <article key={item.cameraId} className="billing-plan-card"><div className="billing-plan-card__header"><div><h3>{item.planName || "No plan"}</h3><span>{item.cameraName || `Camera ${item.cameraId}`}</span></div><strong>{money(item.monthlyPrice || 0)}/mo</strong></div></article>
          ))}
        </div>
      </section>
    </div>
  );
}

export default function BillingHierarchy({ user, onSignOut }: Props) {
  const [context, setContext] = useState<BillingContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const showError = useCallback((value: string) => {
    setError(value);
    setMessage("");
  }, []);
  const showMessage = useCallback((value: string) => {
    setMessage(value);
    setError("");
  }, []);

  useEffect(() => {
    let active = true;
    void getBillingContext()
      .then((value) => active && setContext(value))
      .catch((loadError) => active && showError(loadError instanceof Error ? loadError.message : "Unable to load billing."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [showError]);

  return (
    <div className="billing-page">
      <TopBar onSignOut={onSignOut} email={emailOf(user)} />
      <main className="billing-shell">
        <header className="billing-hero">
          <div className="billing-hero__copy">
            <div className="billing-eyebrow">Qumra billing hierarchy</div>
            <h1>Plans &amp; pricing</h1>
            <p>
              Manage the complete product, partner pricing, and customer access
              flow from one workspace.
            </p>
          </div>
          <div className="billing-hero__aside">
            <span className="billing-role-badge">
              {context?.role || "Billing"}
            </span>
            <div className="billing-hierarchy-flow" aria-label="Billing hierarchy">
              <span>Owner</span>
              <i aria-hidden="true">→</i>
              <span>Integrator</span>
              <i aria-hidden="true">→</i>
              <span>Admin</span>
            </div>
          </div>
        </header>
        {error && <div className="billing-alert billing-alert--error" role="alert">{error}</div>}
        {message && <div className="billing-alert billing-alert--success" role="status">{message}</div>}
        {loading && <div className="billing-loading">Loading billing workspace…</div>}
        {!loading && context?.role === "OWNER" && <OwnerView onError={showError} onMessage={showMessage} />}
        {!loading && context?.role === "INTEGRATOR" && <IntegratorView context={context} onContext={setContext} onError={showError} onMessage={showMessage} />}
        {!loading && context?.role === "ADMIN" && <AdminView onError={showError} />}
        {!loading && context && !["OWNER", "INTEGRATOR", "ADMIN"].includes(context.role) && <div className="billing-empty">Billing management is not available for this account.</div>}
      </main>
    </div>
  );
}
